import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import * as Sentry from '@sentry/react'
import { getSafeSessionStorage } from './common/lib/safeStorage'

// ---------------------------------------------------------------------------
// Step 1 — initialize Sentry BEFORE any app module is loaded.
//
// This is intentional. Until May 2026 main.tsx imported App.tsx (and its
// transitive chain, incl. firebase.ts) at the top of the file. If any module
// in that chain threw at module-load time, main.tsx body never executed and
// Sentry.init was never called. That was the root cause of the white-screen
// reports we couldn't see in Sentry — see docs/white-screen-investigation.md.
//
// The fix: keep only Sentry's own static import at the top, initialize Sentry
// first, THEN dynamic-import the bootstrap module that contains the original
// static import graph. Any throw inside that dynamic import is caught by
// Sentry's now-installed global handlers.
// ---------------------------------------------------------------------------

// Boot-stage marker — mirrors window.__mark installed by index.html's inline
// script. Each milestone the bundle reaches is recorded so a boot that stalls
// AFTER the bundle starts (heavy dynamic import hangs, a module throws, render
// never commits) produces a beacon/Sentry event that says exactly how far it got.
const markBoot = (stage: string): void => {
  try {
    ;(window as unknown as { __mark?: (s: string) => void }).__mark?.(stage)
  } catch {
    /* ignore — telemetry only */
  }
}

markBoot('bundle-eval')

// ---------------------------------------------------------------------------
// Chrome for iOS and the Google app render pages in WKWebView and inject their
// own Closure-compiled user scripts (autofill, password manager, translate) into
// every document. A WKUserScript has no URL of its own, so when one of them
// throws, WebKit blames the *page*: the event arrives with a single frame at
// https://app.oisterbonus.dk/ on a line that does not exist in our HTML (415 for
// a 318-line document) and a message that is a bare minifier token — "fa", "ga",
// "Ba", "Yd", "Aa" — which appears nowhere in our source. Every occurrence picks
// a different token, so each one opens a NEW Sentry group and a new alert
// (OISTER-FRONTEND-2D / 3Q / 3R / 3T); archiving them one by one never ends.
//
// The frame test is what makes dropping them safe: in production every line of
// our own code lives in /assets/*.js, so an exception with no frame there cannot
// be ours. index.html's inline boot script does legitimately throw at the bare
// document URL, but those reach Sentry through flushBootstrapErrors() tagged
// bootstrap_error=true, so they are exempt and keep reporting.
//
// Sibling issue OISTER-FRONTEND-6 ("RangeError: Maximum call stack size
// exceeded.", frame `undefined:196:249`, also 100% iOS WebView) is the same
// family but keeps a real-looking message, so it is deliberately NOT matched
// here — a genuine recursion in our code must stay visible.
// ---------------------------------------------------------------------------
const INJECTED_SCRIPT_TOKEN = /^[A-Za-z]{1,3}$/

function isInjectedUserScriptNoise(event: Sentry.ErrorEvent): boolean {
  if (event.tags?.bootstrap_error === 'true') return false
  const values = event.exception?.values
  const exception = values?.[values.length - 1]
  if (!exception || !INJECTED_SCRIPT_TOKEN.test(exception.value?.trim() ?? '')) return false
  const frames = exception.stacktrace?.frames ?? []
  return !frames.some((frame) => frame.filename?.includes('/assets/'))
}

if (!import.meta.env.DEV) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: true,
    environment: import.meta.env.VITE_SENTRY_ENV ?? 'local',
    integrations: [Sentry.replayIntegration(), Sentry.browserTracingIntegration()],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    ignoreErrors: [
      'Firebase Messaging not supported in this browser',
      'Firebase Messaging not initialized',
      'User denied notification permission',
      'Notifications not supported in browser',
      'This browser does not support notifications',
      /The database connection is closing/i,
      /A mutation operation was attempted on a database that did not allow mutations/i,
      // Same WebKit IndexedDB flakiness as the two entries above, reaching us from
      // inside Firebase's own IDB layer (@firebase/app heartbeat + installations,
      // via getAnalytics) as an opaque DOMException we cannot try/catch — it rejects
      // asynchronously, long after firebase.ts's synchronous guard has returned.
      // Verified against GA4 (30d) that this costs us no analytics data: iOS is 66.3%
      // of web users vs 63.6% on the native app streams, which report through a
      // separate SDK that never touches web IndexedDB. Were events being dropped, the
      // web share would be depressed, not higher. See OISTER-FRONTEND-1A.
      // Anchored on purpose: a real abort carries a message ("AbortError: The user
      // aborted a request") and must keep reporting.
      /^AbortError(: AbortError)?$/,
      /messaging\/unsupported-browser/i,
      /ingesteer\.services-prod\.nsvcs\.net/i,
      /Invalid call to runtime\.sendMessage/i,
      /Object Not Found Matching Id/i,
      /ApplyPageLayout is not defined/i,
    ],

    beforeSend(event, hint) {
      if (isInjectedUserScriptNoise(event)) {
        return null;
      }

      const originalException = hint.originalException;
      const message =
        event.message ||
        (originalException instanceof Error
          ? originalException.message
          : String(originalException || ''));

      const browserSupportWarnings = [
        'not supported',
        'not initialized',
        'denied permission',
        'does not support',
      ];

      const isBrowserSupportWarning = browserSupportWarnings.some((warning) =>
        message.toLowerCase().includes(warning.toLowerCase())
      );

      if (event.level === 'warning' && isBrowserSupportWarning) {
        return null;
      }

      return event;
    },
  })
}

// ---------------------------------------------------------------------------
// Step 2 — flush any errors the inline buffer in index.html captured before
// Sentry was ready (or that an unhandled rejection captured during this very
// tick). Each is tagged `bootstrap_error=true` for filtering in Sentry.
// ---------------------------------------------------------------------------

type BootstrapErrorEntry = {
  kind: string
  message?: string
  stack?: string
  source?: string
  line?: number
  col?: number
  ts?: number
  url?: string
  phase?: string
}

function flushBootstrapErrors(extraTag?: string) {
  const buf = (window as unknown as { __bootstrapErrors?: BootstrapErrorEntry[] })
    .__bootstrapErrors
  if (!Array.isArray(buf) || buf.length === 0) return
  const entries = buf.splice(0, buf.length)
  for (const entry of entries) {
    try {
      // A third-party resource that fails to load is not an application error: it
      // means the user's network blocked it (ad-blocker, DNS filter, corporate
      // proxy). Cookiebot's uc.js alone accounts for ~60 users a week
      // (OISTER-FRONTEND-3F), and the app is designed to survive it — the script is
      // async so it cannot block boot, and consent stays denied by default
      // (consent.ts). There is nothing we can act on, so we DROP it entirely rather
      // than report it (previously reported at warning level, which still kept the
      // issue escalating). Only failures we can act on reach Sentry.
      //
      // Scoped to third-party hosts on purpose: a resource_error on our OWN origin
      // is the entry chunk failing to load, which is exactly the white-screen signal
      // this buffer exists to surface — that keeps reporting at error level, as does
      // every non-resource error (JS throw / unhandled rejection during boot).
      //
      // CAUTION for the future: this is a blanket cross-origin drop. It is safe only
      // because the sole third-party HTML tag today is the async, non-boot-critical
      // Cookiebot uc.js and the entry bundle is served same-origin. If a boot-CRITICAL
      // third-party <script> is ever added to the HTML, or the bundle is moved to a CDN
      // subdomain (Vite `base`), that failure would be silently dropped here — revisit.
      const isThirdPartyResource =
        entry.kind === 'resource_error' &&
        !!entry.source &&
        !entry.source.startsWith(window.location.origin)
      if (isThirdPartyResource) continue
      const err = new Error(entry.message || `Bootstrap error (${entry.kind})`)
      if (entry.stack) err.stack = entry.stack
      Sentry.captureException(err, {
        level: 'error',
        tags: {
          bootstrap_error: 'true',
          bootstrap_phase: entry.phase ?? extraTag ?? entry.kind,
          resource_origin: entry.kind === 'resource_error' ? 'first-party' : 'n/a',
        },
        extra: entry,
      })
    } catch (_) {
      // swallow — last-resort capture, can't recursively re-report
    }
  }
}

flushBootstrapErrors('pre-bundle')

// ---------------------------------------------------------------------------
// Step 2b — handle Vite's `vite:preloadError` for stale-chunk redeploys.
//
// When we deploy a new build, the previous build's hashed chunks are removed
// from Netlify. A client still holding the previous build's index.html then
// imports a chunk that no longer exists and gets "TypeError: Importing a module
// script failed." (OISTER-FRONTEND-2W). We reload to pull a fresh shell, but
// bound the attempts so a genuinely broken deploy can't trap users in a loop.
//
// See docs/white-screen-investigation.md and OISTER-FRONTEND-2W.
// ---------------------------------------------------------------------------

const PRELOAD_RETRY_KEY = 'vite_preload_retries'
const PRELOAD_MAX_RETRIES = 3

// A plain location.reload() is not enough: iOS WKWebView (and CDN edges) keep
// serving the STALE index.html — the one referencing already-deleted chunks —
// even under `Cache-Control: must-revalidate`. Every reload then re-fetches the
// same dead shell, the retry budget burns out, and the user lands on the
// fallback. A unique query string forces a cache MISS at every layer, so the
// reload actually pulls an index.html with current chunk URLs.
const CACHE_BUST_PARAM = '_cb'
// Marker the index.html watchdog appends when it had to cache-bust reload a blank
// shell (entry-chunk 404/MIME failure, or a stalled render-blocking head script).
const WATCHDOG_PARAM = '_wd'

function reloadBypassingCache(attempt: number): void {
  try {
    const url = new URL(window.location.href)
    url.searchParams.set(CACHE_BUST_PARAM, `${attempt}-${Date.now().toString(36)}`)
    // replace() (not reload/assign) avoids stacking history entries across retries.
    window.location.replace(url.toString())
  } catch {
    window.location.reload()
  }
}

// Attempt count comes from the `_cb` param first because it survives reloads
// even when storage is blocked — there the sessionStorage shim is in-memory and
// resets on every load, which would let a real stale-chunk failure loop forever.
// Falls back to storage for the first failure, before any `_cb` exists.
function getReloadAttempt(): number {
  try {
    const cb = new URL(window.location.href).searchParams.get(CACHE_BUST_PARAM)
    if (cb) {
      const n = parseInt(cb.split('-')[0], 10)
      if (Number.isFinite(n)) return n
    }
  } catch {
    /* ignore — fall back to storage */
  }
  return Number(getSafeSessionStorage().getItem(PRELOAD_RETRY_KEY) ?? '0')
}

// Drop the param once we've recovered so it doesn't leak into analytics
// ($current_url) or persist. replaceState does not trigger a navigation.
function clearCacheBustParam(): void {
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.has(CACHE_BUST_PARAM) || url.searchParams.has(WATCHDOG_PARAM)) {
      url.searchParams.delete(CACHE_BUST_PARAM)
      url.searchParams.delete(WATCHDOG_PARAM)
      window.history.replaceState(window.history.state, '', url.toString())
    }
  } catch {
    /* ignore — best-effort cleanup */
  }
}

// If the index.html watchdog had to reload a blank/failed shell, it appended
// `_wd=<attempt>`. We only run once the bundle has successfully loaded — i.e.
// AFTER a recovery — so report it, turning the otherwise-invisible white-screen
// class into a measurable Sentry signal. Param-based (not storage) so it survives
// WebViews that block storage. Call BEFORE clearCacheBustParam drops the param.
function reportWatchdogRecovery(): void {
  try {
    const attempt = new URL(window.location.href).searchParams.get(WATCHDOG_PARAM)
    if (!attempt) return
    const diag = (window as unknown as { __bootDiag?: (r: string) => unknown }).__bootDiag
    Sentry.captureMessage('bootstrap-watchdog-recovered', {
      level: 'warning',
      tags: { bootstrap_error: 'true', bootstrap_phase: 'watchdog-recovered' },
      extra: { attempt: Number(attempt), diag: diag ? diag('watchdog-recovered') : undefined },
    })
  } catch {
    /* ignore — best-effort telemetry */
  }
}

// Set when we trigger a reload to recover from a preload error. Because we then
// preventDefault() the event, Vite swallows the import rejection and the awaited
// import('./bootstrap') below resolves to `undefined` — the IIFE must bail on
// this flag WITHOUT destructuring, otherwise every recoverable stale-chunk
// reload also emitted a misleading "Cannot destructure property 'App'" event
// (OISTER-FRONTEND-2X), inflating that issue far beyond the real failures.
let reloadingAfterPreloadError = false
// Set once retries are exhausted so the IIFE catch can tag the (now accurate,
// re-thrown) chunk-load error as preload-exhausted rather than a generic mount.
let preloadRetriesExhausted = false

window.addEventListener('vite:preloadError', (event) => {
  // Safe storage: a raw sessionStorage access here throws SecurityError when the
  // WebView blocks cookies/storage. Since this handler also fires when the
  // bootstrap import rejects, an unguarded throw here turned a recoverable
  // import failure into an uncaught error → blank screen. See safeStorage.ts.
  const count = getReloadAttempt()
  if (count < PRELOAD_MAX_RETRIES) {
    // Recoverable: swallow Vite's rethrow (preventDefault) and reload. The IIFE
    // bails via the flag, so no spurious error is reported for this case.
    event.preventDefault()
    reloadingAfterPreloadError = true
    // Best-effort/redundant: `_cb` is the source of truth for the attempt count.
    getSafeSessionStorage().setItem(PRELOAD_RETRY_KEY, String(count + 1))
    reloadBypassingCache(count + 1)
    return
  }
  // Retries exhausted — do NOT preventDefault, so Vite rethrows the real error
  // ("Importing a module script failed"). That rejects the awaited import and is
  // reported once by the IIFE catch with an accurate message before the fallback
  // renders — instead of the misleading destructure symptom we used to emit.
  preloadRetriesExhausted = true
})

// ---------------------------------------------------------------------------
// Step 3 — single dynamic import of the bootstrap module. The original static
// import graph lives inside ./bootstrap; preserving that graph (rather than
// fanning out into Promise.all of separate dynamic imports) avoids breaking
// any existing circular-import ordering. Any throw inside this chain is
// caught here and reported to Sentry with `bootstrap_error=true`.
// ---------------------------------------------------------------------------

;(async () => {
  try {
    const bootstrap = await import('./bootstrap')

    // A recoverable preload error already triggered a reload; after our
    // preventDefault() Vite resolved this import to `undefined`. Bail before
    // destructuring so we don't report a misleading error while the page reloads.
    if (reloadingAfterPreloadError || !bootstrap) return

    const { App, runBootstrapSideEffects } = bootstrap
    markBoot('bootstrap-loaded')

    // Recovered after a cache-busting reload — report a watchdog recovery (if the
    // inline watchdog triggered it) then drop the params before analytics
    // initialises inside runBootstrapSideEffects and captures $current_url.
    reportWatchdogRecovery()
    clearCacheBustParam()

    runBootstrapSideEffects()

    // Flush again in case anything fired between Sentry.init and now.
    flushBootstrapErrors('app-import')

    markBoot('react-render')
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    )
    // Marked after paint: proves React actually committed, not just that render()
    // was called — a beacon showing stage 'react-render' but never 'mounted' means
    // the tree threw during its first commit.
    requestAnimationFrame(() => markBoot('mounted'))

    // Successful mount — reset the preload-error retry counter so an
    // unrelated future stale-chunk incident gets a full budget of retries.
    getSafeSessionStorage().removeItem(PRELOAD_RETRY_KEY)
  } catch (error) {
    // Module-load or render failure. Surface to Sentry with bootstrap tag and
    // render a minimal fallback so the user never sees a pure white screen.
    const diag = (window as unknown as { __bootDiag?: (r: string) => unknown }).__bootDiag
    const diagSnapshot = diag ? (diag('app-mount-error') as { os?: string } | undefined) : undefined
    // Mirror index.html's beacon severity policy so scanner-only mount throws stop
    // raising error-level alerts: 'hard' automation (webdriver / headless / crawler UA —
    // shared detector) OR a desktop OS (the OVH "Edge 122" fleet hitting Promise.allSettled,
    // OISTER-FRONTEND-3P, plus Safe Links) → warning. Keyed on __bootDiag's parsed OS, the
    // same source the beacon uses, so every real iOS/Android user keeps reporting at error
    // and their genuine app-mount / preload-exhausted failures still alert.
    const botHard =
      (window as unknown as { __botLevel?: () => string }).__botLevel?.() === 'hard'
    const botSuspected = botHard || /^(Windows|Mac OS X|CrOS|Linux)/.test(diagSnapshot?.os ?? '')
    Sentry.captureException(error, {
      level: botSuspected ? 'warning' : 'error',
      tags: {
        bootstrap_error: 'true',
        bootstrap_phase: preloadRetriesExhausted ? 'preload-exhausted' : 'app-mount',
        boot_stage: (window as unknown as { __bootStage?: string }).__bootStage ?? 'unknown',
        bot_suspected: String(botSuspected),
      },
      extra: { diag: diagSnapshot },
    })
    console.error('[Bootstrap] Critical failure during app mount:', error)

    const root = document.getElementById('root')
    if (root && root.innerHTML.length === 0) {
      root.innerHTML = `
        <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;font-family:system-ui,-apple-system,sans-serif;background:#f9fafb;">
          <div style="max-width:420px;text-align:center;">
            <h1 style="font-size:20px;margin:0 0 12px;color:#111827;">Noget gik galt</h1>
            <p style="font-size:14px;margin:0 0 20px;color:#4b5563;">Der opstod en fejl ved opstart af appen. Prøv venligst at genindlæse siden.</p>
            <button onclick="window.__bootCleanReload ? window.__bootCleanReload() : location.reload()" style="background:#503595;color:#fff;border:0;border-radius:9999px;padding:12px 24px;font-size:15px;cursor:pointer;">Prøv igen</button>
          </div>
        </div>
      `
    }
  }
})()
